import isEmpty from 'lodash/isEmpty';
import mergeWith from 'lodash/mergeWith';

// Customizer function for `lodash`'s `mergeWith` method;
// See the docs [here](https://lodash.com/docs#merge)
function recordMergeCustomizer(objValue: unknown, srcValue: unknown) {
  return [objValue, srcValue].filter((value) => typeof value === 'string' && value != '').join(' ');
}

/**
 * Merge any number of `Record`s containing CSS classnames.
 * 
 * The order of classes has NO effect on how styles are applied. 
 * 
 * CSS styles will be overridden by any styles with selectors of equal specificity that are mounted lower in the DOM. Also, keys from the result that refer to values of `null`, `undefined`, empty strings, etc will be filtered out.
 * 
 * i.e. 
```html
<style> .blue { color: blue }</style>
<style> .red { color: red }</style>
<p class="red blue">This is red because that style was defined below the 'blue' style</p>
<p class="blue red">This is still red</p>
```
 * 
 * examples:
```ts
console.log(mergeClasses({ root: 'classname-1' },
                           { root: 'classname-2' }));
// will log { root: 'classname-1 classname-2' }

console.log(mergeClasses({ a: 'classA' },
                           { b: 'classB' },
                           null,
                           { b: null }));
// will log { a: 'classA', b: 'classB' }
```
 */
export function mergeClasses(...classes: Record<string, string>[]) {
  const result = classes
    .filter((el) => el != null)
    .reduce((accumulatedClasses, currentClasses) =>
      mergeWith(accumulatedClasses, currentClasses, recordMergeCustomizer),
    );

  Object.entries(result).forEach(([key]) => {
    if (result && key && result[key]) {
      const newResult = [...new Set(result[key].split(' '))].join(' ');
      result[key] = newResult;
    }
  });

  // remove keys that refer to values of `null`, `undefined`, empty strings, etc.
  Object.entries(result).forEach(([key, value]) => {
    if (isEmpty(value)) {
      delete result[key];
    } else {
      result[key] = value.trim();
    }
  });
  return result;
}
